/****************************************************************
 * helper functions for the related components
 ***************************************************************/

import { getItemFromLocalStorage } from 'lib/helper';

import history from '../../history.js';

export const GITHUB_URL = process.env.REACT_APP_GIT_URL;
export const BITBUCKET_URL = process.env.REACT_APP_BITBUCKET_URL;
export const GITLAB_URL = process.env.REACT_APP_GITLAB_URL;
export const AZURE_URL = process.env.REACT_APP_AZURE_URL;

export enum HostType {
  Azure = 'azure',
  Github = 'github',
  Gitlab = 'gitlab',
  Bitbucket = 'bitbucket',
  // Other = 'other',
}

export enum RepositoryFilterOptions {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
}

export const BASE_URL: string = getItemFromLocalStorage('approle') || '';

export const getOAuthUrl = (
  hostType: HostType,
  connectionType: 'new' | 'revive',
  accountId: number | undefined = undefined,
) => {
  type UrlMap = {
    [key in HostType]: string | undefined;
  };

  const urlMap: UrlMap = {
    [HostType.Azure]: AZURE_URL,
    [HostType.Github]: GITHUB_URL,
    [HostType.Gitlab]: GITLAB_URL,
    [HostType.Bitbucket]: BITBUCKET_URL,
    // [HostType.Other]: undefined, // Add other host types if needed // TODO: UNCOMMENT LATER FOR ON PREMISES
  };

  const oAuthUrl = urlMap[hostType];

  if (oAuthUrl) {
    return updateAuthUrlStateParam(oAuthUrl, connectionType, accountId);
  } else {
    // eslint-disable-next-line no-console
    console.error(`${hostType} oAuth URL is not defined.`);
    return '';
  }
};

export interface State {
  hostType: string;
  mode: string;
  accountId?: number;
}

/**
 * @param url
 * // Example usage
 * const originalUrl = "https://app.vssps.visualstudio.com/oauth2/authorize?client_id=CLIENTID&response_type=Assertion&state=azureapp&scope=vso.agentpools%20vso.build%20vso.code%20vso.identity%20vso.profile_write&redirect_uri=https://localhost:3000/guest/sast";
 * const newStateValue = "review";
 * const updatedUrl = updateStateParam(originalUrl, newStateValue);
 *
 * @param newState
 * @returns updated url
 */
export const updateAuthUrlStateParam = (
  url: string | undefined,
  mode: string,
  accountId: number | undefined = undefined,
): string | undefined => {
  if (!url) return url;

  const urlObject = new URL(url);
  const searchParams = new URLSearchParams(urlObject.search);

  // Update the state parameter
  const currentState = searchParams.get('state');
  // translate old state to hostType
  let hostTypeValue = currentState;
  switch (currentState) {
    case 'githubapp':
      hostTypeValue = 'github';
      break;
    case 'gitlabapp':
      hostTypeValue = 'gitlab';
      break;
    case 'azureapp':
      hostTypeValue = 'azure';
      break;
    case 'bitbucketapp':
      hostTypeValue = 'bitbucket';
      break;
    default:
      hostTypeValue = currentState;
      break;
  }
  if (hostTypeValue) {
    const newState: State = {
      hostType: hostTypeValue,
      mode: mode,
    };

    if (accountId) {
      newState.accountId = accountId;
    }
    searchParams.set('state', encodeURIComponent(JSON.stringify(newState)));
  }

  // Reconstruct the URL with the updated search parameters
  urlObject.search = searchParams.toString();
  return urlObject.toString();
};

export const getOAuthStateData = ():
  | { code: string; state: State }
  | undefined => {
  const searchParams = new URLSearchParams(window.location.search);
  const stateParam = searchParams.get('state');
  const code = searchParams.get('code');

  // console.log('in: getOAuthStateData', window.location, searchParams, stateParam, code)

  if (stateParam && code) {
    const decodedData = decodeURIComponent(stateParam);
    return { code: code, state: JSON.parse(decodedData) };
  }

  return undefined;
};

// eslint-disable-next-line no-warning-comments
// TODO: move to new SAST:
// eslint-disable-next-line no-warning-comments
//  TODO: remove from old sast listing: links to add new accounts + actions
export const handleSastOAuthReceived = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const stateParam = searchParams.get('state');
  const code = searchParams.get('code');

  // console.log({
  //     msg: 'in: handleSastOAuthReceived',
  //     searchParams,
  //     stateParam,
  //     code,
  // });

  if (!code || code.length === 0) return false;

  if (stateParam) {
    const decodedState = decodeURIComponent(stateParam);

    try {
      const stateObject = JSON.parse(decodedState);
      //
      if (stateObject && stateObject.hostType && stateObject.mode) {
        history.push(
          `/${BASE_URL}/sast?code=${code}&state=${stateParam}&t=new`,
        );
        return true;
      } else {
        // eslint-disable-next-line no-console
        console.error('Invalid state, expected params missing');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing JSON state:', error);
      // ignore.
    }
  } else {
    // eslint-disable-next-line no-console
    console.info("No 'state' parameter found in the URL.");
  }
  return false;
};

export const getHostLabel = (type: HostType) => {
  switch (type) {
    case 'azure':
      return 'Azure DevOps';
    case 'github':
      return 'Github';
    case 'gitlab':
      return 'Gitlab';
    case 'bitbucket':
      return 'Bitbucket';
    // TO DO: UNCOMMENT LATER FOR ON PREMISES
    // case "other": return "On Premises";
    default:
      return '';
  }
};

export function getEnumKeyByEnumValue<T extends { [index: string]: string }>(
  myEnum: T,
  enumValue: string,
): keyof T {
  const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
  return keys[0];
}

/**
 * Updates the 't' query parameter in the URL without navigating away
 * @param tabName - The tab name to set in the query parameter (e.g., 'account', 'repo')
 */
export const updateTabQueryParam = (tabName: string) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('t', tabName);
  history.replace(
    `${window.location.pathname}?${searchParams.toString()}${window.location.hash}`,
  );
};
